package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.core.Page
import dev.lingvo.shared.Plan
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.DocsSubheading
import dev.lingvo.site.components.marketing.MarketingDocPage
import dev.lingvo.site.components.marketing.formatThousands
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem

/**
 * The terms of service.
 *
 * The plan figures quoted here come from [Plan], the same enum the server bills from, so the terms
 * cannot promise an allowance the product does not actually grant.
 */
@Page("/terms")
@Composable
fun TermsPage() {
    LaunchedEffect(Unit) {
        document.title = tr("terms.terms_lingvo", "Terms — Lingvo")
    }
    MarketingDocPage(
        eyebrow = "Terms of service",
        title = t("terms.the_deal_in_the_fewest_words", "The deal, in the fewest words that still mean something."),
        lede = "These terms cover using Lingvo. They are written to be read once and understood, " +
            "which means they say what happens rather than reserving every right imaginable.",
        entries = termsEntries(),
        footnote = "Last updated 10 August 2026. Lingvo is operated from Australia and these terms " +
            "are governed by Australian law. Material changes are announced by email to workspace " +
            "owners at least 30 days before they take effect.",
    )
}

internal fun termsEntries(): List<DocEntry> = listOf(
    DocEntry("agreement", tr("terms.the_agreement", "The agreement")) {
        DocsProse(
            tr("terms.by_creating_an_account_you_agree", "By creating an account you agree to these terms. If you are agreeing on behalf of a " +
                "company, you are confirming you may bind it. If you do not agree, do not create " +
                "an account — and if you already have one, close it and export your data first."),
        )
    },

    DocEntry("account", tr("terms.your_account", "Your account")) {
        DocsList(
            listOf(
                "You are responsible for what happens under your credentials, so keep them safe.",
                "One account per person. Sharing a login defeats the activity trail your own team " +
                    "relies on.",
                "API tokens carry your access. Treat them like passwords; revoke any you suspect.",
                "If an account is compromised we can end its sign-ins, API tokens and agent " +
                    "connections without waiting for you — see Support actions below.",
                "You must be old enough to enter a contract where you live.",
            ),
        )
    },

    DocEntry("content", tr("terms.your_content_stays_yours", "Your content stays yours")) {
        DocsProse(
            tr("terms.you_own_the_keys_strings_translations", "You own the keys, strings, translations and glossary terms you put into Lingvo. We " +
                "claim no rights over them beyond the ones needed to run the service for you: " +
                "storing them, showing them to your workspace members, and — only when you ask — " +
                "sending them to the machine-translation provider you chose."),
        )
        DocsProse(
            tr("terms.we_do_not_train_models_on", "We do not train models on your content and we do not sell it. You can export " +
                "everything at any time, on any plan, including free."),
        )
    },

    DocEntry("acceptable-use", tr("terms.acceptable_use", "Acceptable use")) {
        DocsProse(tr("terms.do_not_use_lingvo_to", "Do not use Lingvo to:"))
        DocsList(
            listOf(
                "Store or distribute content that is illegal where you or we operate.",
                "Break, overload or probe the service, or work around plan limits and rate limits.",
                "Resell access, or run a translation bureau for third parties on a single seat.",
                "Upload content you have no right to upload.",
            ),
        )
        DocsProse(
            tr("terms.we_will_contact_you_before_acting", "We will contact you before acting on anything ambiguous. We will act first, and " +
                "explain after, only where waiting would cause real harm — active abuse, a " +
                "compromised account, or a legal obligation."),
        )
    },

    DocEntry("support-actions", tr("terms.what_support_can_do_in_your", "What support can do in your workspace")) {
        DocsProse(
            tr("terms.when_you_ask_us_about_something", "When you ask us about something, a person at Lingvo can do a small, fixed set of " +
                "things inside your workspace: add machine-translation credits, end a stuck " +
                "translation run and return the credits it reserved, and re-run one of our own " +
                "scheduled jobs. Adding credits shows up on your audit page with the reason."),
        )
        DocsProse(
            tr("terms.we_can_also_end_every_sign", "We can also end every sign-in, API token and agent connection belonging to one " +
                "person, without asking first. We do this when we have reason to think an account " +
                "is compromised, and the person is emailed as soon as it is done. Nothing is " +
                "deleted and no workspace access is removed — only the credentials stop working, " +
                "and signing in again restores everything except API tokens and agent connections, " +
                "which have to be created again."),
        )
        DocsProse(
            tr("terms.we_cannot_sign_in_as_you", "We cannot sign in as you, read your password, read an API token, or read a provider " +
                "key. Those are not policies we could change our minds about; there is no route " +
                "and no stored value that would make any of them possible."),
        )
    },

    DocEntry("plans", tr("terms.plans_seats_and_credits", "Plans, seats and credits")) {
        DocsProse(
            tr("terms.lingvo_bills_per_owner_and_admin", "Lingvo bills per owner and admin seat. Translators and viewers are free and unlimited, " +
                "on every plan. Keys, languages and words are unlimited on every plan, including " +
                "free — we do not meter the thing you came here to store."),
        )
        // **Length multiplies the engine's rate. It does not add one credit to it.**
        //
        // `creditsForString` is `creditCost(provider) * units`, where `units` is the source length
        // divided by 1,000 and rounded up. "Each further 1,000 characters costs one more credit"
        // describes that correctly only for the one-credit engines: a 1,500-character string on
        // Claude is charged 3 x 2 = 6, and this sentence promised 4. A 50% understatement, in the
        // terms of service, for the two engines a Free workspace reaches by default.
        DocsProse(
            tr("terms.machine_translation_is_the_one_metered", "Machine translation is the one metered feature. One credit is one string of up to " +
                "1,000 characters translated into one language; Claude and OpenAI cost three, " +
                "because they cost us more. Length multiplies that rate rather than adding to it: " +
                "a string over 1,000 characters counts as two, so it costs two credits on DeepL, " +
                "Google, LibreTranslate or MyMemory, and six on Claude or OpenAI. Strings longer " +
                "than 2,000 characters are not machine-translated at all. A plural is charged " +
                "once, for its longest form. Credits reset each calendar month and do not roll " +
                "over. A project using its own provider key spends no credits."),
        )
        DocsProse(
            tr("terms.we_can_also_add_credits_to", "We can also add credits to a workspace at no charge — usually to put back credits a " +
                "failed run consumed, or after a billing mistake. Added credits behave exactly " +
                "like the ones your plan includes: they belong to one calendar month and they do " +
                "not roll over. They can never produce a charge, and a workspace on Free that is " +
                "given credits is still on Free."),
        )
        DocsSubheading(tr("terms.what_each_plan_includes", "What each plan includes"))
        // **`"$it project" + if (it == 1) "" else "s"` is English's plural rule with a
        // general-sounding shape**, doing the counting on the page that sells real plurals to a
        // localization audience. It is wrong in most languages, and it was the only counted string
        // in this file. `tPlural` asks the reader's own language which form a count selects.
        //
        // The row around it is assembled by substitution rather than interpolation for the reason
        // the glossary panel already records: a key built by interpolation is invisible to the
        // catalogue extractor and can never be translated, so a row half-translated by `tPlural`
        // inside an English sentence would read worse than the uniform English it replaced.
        DocsList(
            Plan.entries.map { plan ->
                val price = if (plan.pricePerSeat == 0) {
                    tr("terms.no_charge", "no charge")
                } else {
                    trf(
                        "terms.amount_per_owner_seat_per_month",
                        "{amount} per owner seat per month",
                        "amount" to "US\$${plan.pricePerSeat}",
                    )
                }
                val seats = plan.maxBillableSeats?.let {
                    tPlural("terms.billable_seats", it, "{count} billable seat", "{count} billable seats")
                } ?: tr("terms.unlimited_billable_seats", "unlimited billable seats")
                val projects = plan.maxProjects?.let {
                    tPlural("terms.projects", it, "{count} project", "{count} projects")
                } ?: tr("terms.unlimited_projects", "unlimited projects")
                trf(
                    "terms.plan_row",
                    "{plan} — {price}, {credits} credits per seat each month, {seats}, {projects}.",
                    "plan" to plan.displayName,
                    "price" to price,
                    "credits" to formatThousands(plan.creditsPerSeat),
                    "seats" to seats,
                    "projects" to projects,
                )
            },
        )
        LAlert(
            title = t("terms.free_never_becomes_a_bill_by", "Free never becomes a bill by surprise"),
            description = tr("terms.the_free_plan_is_a_hard_2", "The free plan is a hard cap, not an overage. When the credits run out, " +
                "machine translation stops until the month turns over. Translating by hand, " +
                "importing, exporting, QA and the CLI all keep working."),
            modifier = Modifier.fillMaxWidth().maxWidth(44.cssRem),
            variant = LAlertVariant.SUCCESS,
        )
    },

    DocEntry("billing", tr("terms.billing", "Billing")) {
        DocsList(
            listOf(
                "Paid plans are charged monthly in advance, per billable seat.",
                "Seat changes are pro-rated immediately, both ways: adding an owner or admin " +
                    "mid-month is charged for the rest of that month, and removing one is credited " +
                    "against the next invoice.",
                "Prices are in US dollars and exclude any tax we are required to collect.",
                "Cancel whenever you like — the plan runs to the end of the period you paid for, " +
                    "then stops. We do not refund partial months, and we do not lock your data " +
                    "behind an active subscription.",
                "If a payment fails we will tell you before anything changes.",
            ),
        )
    },

    DocEntry("availability", tr("terms.availability_and_support", "Availability and support")) {
        DocsProse(
            tr("terms.lingvo_is_a_small_independent_product", "Lingvo is a small, independent product. It is provided as it is, without a contractual " +
                "uptime guarantee — saying otherwise would be a promise we could not back with a " +
                "credit scheme."),
        )
        DocsProse(
            tr("terms.what_we_do_commit_to_we", "What we do commit to: we tell you about incidents rather than hiding them, we do not " +
                "delete data to save money, and if the service is ever shut down you get at least " +
                "90 days' notice and a working export the whole time."),
        )
    },

    DocEntry("termination", tr("terms.ending_it", "Ending it")) {
        // **Settings → Billing → Cancel, and it has to name the owner.**
        //
        // This said "Settings → Plan and usage", which is not a workspace settings page and never
        // was: `AppNav.SETTINGS` offers Billing and Usage, and "Plan and usage" is a *project*
        // settings tab. So a customer following this sentence opened Settings, found neither of
        // those words, and had to hunt — on the one page a terms document exists to make
        // unambiguous. `Checkout.kt` already points at "Settings → Billing → Compare plans" for
        // upgrading; these two are the pair that drifted.
        //
        // "An owner" rather than "you": `CancelSubscriptionPage` refuses an admin outright, because
        // a Paddle subscription belongs to the person who bought it. Promising an admin they can
        // cancel themselves sends them to a page that tells them they cannot.
        DocsProse(
            tr("terms.an_owner_can_cancel_a_paid", "An owner can cancel a paid plan at any time, from Settings → Billing → Cancel. " +
                "The plan runs to the end of the period you paid for, then the workspace returns " +
                "to Free. Nothing is deleted."),
        )
        // **Both of these shipped, and this paragraph did not notice.**
        //
        // It said closing an account and deleting a workspace "are not yet self-service. Email
        // support@lingvo.dev" — while `/app/account/close` and the danger zone on
        // `/app/settings/general` both exist and both work. The closing line, "we would rather say
        // that plainly than offer a button that is not there", had inverted: the buttons are there
        // and the terms were denying them. A page whose whole claim is that it describes the real
        // system is the worst place in the product for a sentence that has fallen a release behind.
        DocsProse(
            tr("terms.closing_your_account_is_self_service", "Closing your account is self-service, at Settings → Your account → Close your " +
                "account. It works out what closing would do before anything happens — which " +
                "workspaces you are the only owner of, and how many tokens stop working. Nothing " +
                "is deleted for seven days after you confirm, and one click during those seven " +
                "days brings it all back. Your translations stay; your name comes off them."),
        )
        DocsProse(
            tr("terms.deleting_a_workspace_is_self_service", "Deleting a workspace is self-service too, for an owner, at Settings → Workspace. " +
                "Cancel the subscription first: deleting a workspace does not stop a payment, so " +
                "that page refuses until the plan is ended. Export before either — it needs no " +
                "plan and no permission from us. Email support@lingvo.dev if you would rather we " +
                "did any of it for you."),
        )
        DocsProse(
            tr("terms.we_may_suspend_or_close_an", "We may suspend or close an account that breaks the acceptable-use section, or that " +
                "has not paid. Except where the law or active abuse prevents it, we will give you " +
                "notice and a window to export first."),
        )
    },

    DocEntry("liability", tr("terms.liability", "Liability")) {
        DocsProse(
            tr("terms.to_the_extent_the_law_allows", "To the extent the law allows, Lingvo is not liable for indirect or consequential loss, " +
                "and our total liability in any 12-month period is limited to what you paid us in " +
                "that period. Nothing here excludes rights that cannot be excluded — including " +
                "consumer guarantees under Australian Consumer Law."),
        )
        DocsProse(
            tr("terms.machine_translation_output_is_a_draft", "Machine translation output is a draft. It is not reviewed by us, it can be wrong, and " +
                "shipping it unreviewed is your decision — which is exactly why the editor puts a " +
                "review step in front of it."),
        )
    },

    DocEntry("contact-terms", tr("terms.contact", "Contact")) {
        DocsProse(tr("terms.questions_about_these_terms_go_to", "Questions about these terms go to support@lingvo.dev."))
    },
)
